import React, { useEffect, useState } from "react";
import { Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import apiClient from "../../../api/axios";
import BannerFormDialog from "./bannerFormDialog";

// Lists banners from /banners; expand with pagination/filters later
const AppBannerList = ({ refreshTrigger }) => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);

    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const response = await apiClient.get("/banners/");
                const sortedBanners = (response.data || []).sort((a, b) => {
                    // First sort by priority (ascending)
                    if (a.priority !== b.priority) {
                        return a.priority - b.priority;
                    }
                    // Then sort by active status (active first)
                    if (a.is_active !== b.is_active) {
                        return a.is_active ? -1 : 1;
                    }
                    return 0;
                });
                setBanners(sortedBanners);
            } catch (err) {
                setError("Failed to load banners");
            } finally {
                setLoading(false);
            }
        };

        fetchBanners();
    }, [refreshTrigger]);

    const formatDate = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
    };

    const renderMaxShows = (value) => {
        if (value === 0) return "Always";
        if (value === null || value === undefined) return "-";
        return value;
    };

    const renderActiveIcon = (flag) =>
        flag ? (
            <CheckCircleOutlineIcon style={{ color: "#2e7d32" }} />
        ) : (
            <HighlightOffIcon style={{ color: "#c62828" }} />
        );

    const handleEdit = (banner) => {
        setSelectedBanner(banner);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedBanner(null);
    };

    const handleSuccess = async () => {
        // Refresh the banner list
        setLoading(true);
        try {
            const response = await apiClient.get("/banners/");
            const sortedBanners = (response.data || []).sort((a, b) => {
                if (a.priority !== b.priority) {
                    return a.priority - b.priority;
                }
                if (a.is_active !== b.is_active) {
                    return a.is_active ? -1 : 1;
                }
                return 0;
            });
            setBanners(sortedBanners);
        } catch (err) {
            setError("Failed to load banners");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: "24px" }}>
            <h2>App Banners</h2>
            {loading && <p>Loading banners…</p>}
            {error && <p style={{ color: "red" }}>{error}</p>}
            {!loading && !error && banners.length === 0 && <p>No banners found.</p>}
            {!loading && !error && banners.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Title</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Body</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Image</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Priority</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Max Shows</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Active</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Type</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>Start</th>
                                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>End</th>
                                <th style={{ textAlign: "center", padding: "8px", borderBottom: "1px solid #ddd" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {banners.map((banner) => (
                                <tr key={banner.id}>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{banner.title}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{banner.body}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
                                        {banner.image_url ? (
                                            <img
                                                src={`${banner.image_url}`}
                                                alt={banner.title || "banner"}
                                                style={{ width: "120px", objectFit: "cover", borderRadius: "4px" }}
                                            />
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{banner.priority}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{renderMaxShows(banner.max_shows)}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{renderActiveIcon(banner.is_active)}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{banner.type || "-"}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{formatDate(banner.start_time)}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{formatDate(banner.end_time)}</td>
                                    <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0", textAlign: "center" }}>
                                        <IconButton size="small" color="primary" onClick={() => handleEdit(banner)}>
                                            <EditIcon />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <BannerFormDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                banner={selectedBanner}
                onSuccess={handleSuccess}
            />
        </div>
    );
};

export default AppBannerList;
